import { CallLanguage, PreferredLanguage } from './language';
import type { CurrencyCode } from './money';
import { getPrice, type PricedPackageId } from './pricing';

// Domain entities - tipos base

export type PaymentProvider = "dodo" | "stripe" | "revenuecat";

// Canonical set of stored Transaction providers — ÚNICA fuente de verdad para el
// tipo del dominio Y el mapeador de lectura de infraestructura. Derivar ambos de
// esta constante impide que añadir un provider a la unión se desincronice del
// normalizador (esa deriva hacía que 'referral' volviera como 'legacy' al leer).
export const TRANSACTION_PROVIDERS = ["dodo", "stripe", "revenuecat", "admin", "legacy", "referral"] as const;
export type TransactionProvider = (typeof TRANSACTION_PROVIDERS)[number];

/** Type guard sobre el conjunto canónico de providers (lo usa el read-mapper). */
export const isTransactionProvider = (value: unknown): value is TransactionProvider =>
  typeof value === "string" && (TRANSACTION_PROVIDERS as readonly string[]).includes(value);

export const PAYMENT_PROVIDERS = {
  DODO: "dodo" as PaymentProvider,
  /** Proveedor web alternativo a Dodo (ADR-030): mismo puerto, conmutado por `PAYMENT_PROVIDER`. */
  STRIPE: "stripe" as PaymentProvider,
  REVENUECAT: "revenuecat" as PaymentProvider,
};

/**
 * Qué es una compra PAGADA de verdad, como dato: liquidada (`completed`) y de
 * un proveedor de pago, así que quedan fuera 'referral', 'admin' y 'legacy'.
 * Los proveedores se derivan de PAYMENT_PROVIDERS para que uno nuevo (Stripe,
 * ADR-030) no se quede fuera por un literal olvidado. `isPaidPurchase` y la
 * consulta del repositorio se construyen las dos a partir de aquí.
 */
export const PAID_PURCHASE: {
  readonly status: TransactionStatus;
  readonly providers: readonly PaymentProvider[];
} = {
  status: "completed",
  providers: Object.values(PAYMENT_PROVIDERS),
};

export function isPaidPurchase(transaction: Pick<Transaction, "status" | "provider">): boolean {
  return (
    transaction.status === PAID_PURCHASE.status &&
    (PAID_PURCHASE.providers as readonly TransactionProvider[]).includes(transaction.provider)
  );
}

export interface Parent {
  id: string;
  email: string;
  pinHash: string | null;
  availableMinutes: number;
  preferredLanguage: PreferredLanguage;
  followUpSentAt?: Date | null;
  followUpDiscountCode?: string | null;
  // Programa de referidos (columnas desde 01-01). `referralCode` es el código
  // opaco y estable del padre para compartir; `referredByCode` sella la
  // atribución del invitado en su creación (server-side, una vez).
  referralCode?: string | null;
  referredByCode?: string | null;
  // Marca del despliegue en el que se dio de alta (ADR-035). null = santa implícito
  // (padres anteriores a la columna). Solo se escribe al crear; nunca se pisa.
  signupBrand?: string | null;
  // Consentimiento de marketing por email (ADR-047). `marketingConsentAt`: cuándo
  // se dio, null = nunca o retirado. `marketingUnsubscribedAt`: última baja.
  marketingConsentAt?: Date | null;
  marketingUnsubscribedAt?: Date | null;
  createdAt: Date;
}

export interface FollowUpCandidate {
  parent: Parent;
  latestChildCreatedAt: Date;
  childName?: string;
}

export interface Child {
  id: string;
  parentId: string;
  name: string;
  age: number;
  gender: "male" | "female" | "other";
  language: CallLanguage;
  hobbies?: string | null;
  achievements?: string | null;
  behavior?: "excellent" | "good" | "needs_improvement" | null;
  pets?: string | null;
  siblings?: string | null;
  christmasWishes?: string | null;
  additionalInfo?: string | null;
  heygenContextId?: string | null;
  createdAt: Date;
}

// Canonical set of call statuses: the type derives from it so a new status is
// declared once and every exhaustive `Record<CallStatus, …>` must handle it.
export const CALL_STATUSES = [
  "reserved",
  "reservation_failed",
  "in_progress",
  "completed",
  "failed",
] as const;

export type CallStatus = (typeof CALL_STATUSES)[number];

export function isCallStatus(value: unknown): value is CallStatus {
  return (CALL_STATUSES as readonly unknown[]).includes(value);
}

/** Read boundary: a status outside the set is a data bug, not something to paper over. */
export function assertCallStatus(value: unknown): CallStatus {
  if (!isCallStatus(value)) {
    throw new Error(
      `Estado de llamada desconocido: «${String(value)}» (declarados: ${CALL_STATUSES.join(", ")}).`
    );
  }
  return value;
}

/**
 * Motivo por el que el padre prepara ESTA llamada.
 *
 * Vive en Call y no en Child a propósito. `christmasWishes` está en Child
 * porque es un rasgo del niño que dura toda la temporada; un cumpleaños, en
 * cambio, es de la llamada del 12 de marzo: ponerlo en el perfil lo dejaría
 * pegado al niño para siempre y la llamada de mayo seguiría felicitándole.
 *
 * `none` es explícito (no un NULL) para distinguir "el padre eligió que no hay
 * ocasión" de "fila anterior a la migración, no lo sabemos".
 */
export const CALL_OCCASIONS = ["birthday", "achievement", "none"] as const;

export type CallOccasion = (typeof CALL_OCCASIONS)[number];

/**
 * Guard del set cerrado. Es la única puerta de entrada del valor: las rutas de
 * API lo usan para rechazar cualquier otra cosa antes de que llegue al prompt.
 */
export function isCallOccasion(value: unknown): value is CallOccasion {
  return (CALL_OCCASIONS as readonly unknown[]).includes(value);
}

export interface Call {
  id: string;
  parentId: string;
  childId: string;
  childName: string;
  characterId?: string | null;
  startedAt: Date;
  // Server timestamp written once when the provider connection is confirmed.
  // NULL on legacy rows and calls that never connected.
  connectedAt?: Date | null;
  endedAt?: Date | null;
  durationSeconds: number;
  status: CallStatus;
  isTrial: boolean;
  // Minutes the parent configured for the call. NULL on legacy rows created
  // before atomic reservation (issue #80); treated as "nothing reserved".
  configuredMinutes?: number | null;
  // Minutes atomically debited and held at reserve time, refunded on settle.
  // Defaults to 0 so legacy/in-flight rows neither double-charge nor crash.
  reservedMinutes: number;
  // Qué se celebra en la llamada. NULL en filas legacy (previas a la columna).
  occasion?: CallOccasion | null;
  // Detalle libre que el padre cuenta sobre la ocasión ("ha aprendido a nadar").
  // Texto libre del usuario: se sanea antes de interpolarse en el prompt
  // (server/application/prompt-safety.ts), como los campos del perfil.
  occasionNote?: string | null;
}

// Canonical "blocking" (live) call statuses: a parent may hold at most ONE call
// in these states at a time, enforced by the `one_active_call_per_parent`
// partial unique index. This single source is reused by the Prisma adapter, the
// in-memory test double, and the active-call lookups so the rule is declared
// exactly once (issue #80).
export const BLOCKING_CALL_STATUSES: readonly CallStatus[] = [
  "reserved",
  "in_progress",
];

const BLOCKING_CALL_STATUS_SET: ReadonlySet<string> = new Set(
  BLOCKING_CALL_STATUSES
);

/** True when a call still occupies the one-blocking-call-per-parent slot. */
export const isBlockingCallStatus = (status: string): boolean =>
  BLOCKING_CALL_STATUS_SET.has(status);

// Canonical terminal call statuses: once a call reaches one of these, the
// settlement/release paths treat it as immutable (idempotency guard — a retry
// must neither double-charge nor double-refund, and a released reservation can
// never be flipped back to completed). Single source for the Prisma adapter and
// the in-memory test double, like BLOCKING_CALL_STATUSES above (issue #80).
export const TERMINAL_CALL_STATUSES: readonly CallStatus[] = [
  "completed",
  "failed",
  "reservation_failed",
];

const TERMINAL_CALL_STATUS_SET: ReadonlySet<string> = new Set(
  TERMINAL_CALL_STATUSES
);

/** True when a call has reached a terminal state (no further money movement). */
export const isTerminalCallStatus = (status: string): boolean =>
  TERMINAL_CALL_STATUS_SET.has(status);

/**
 * Una llamada «con conversación»: completada y con duración. Una que nunca
 * conectó se liquida a 0 s y no es una llamada para el padre. ÚNICA definición:
 * la usan el agregado de los repositorios (`completedCallSummary`) y la decisión
 * de emitir `call_completed`, para que el evento y `calls_count` digan lo mismo.
 */
export const CONVERSED_CALL_MIN_SECONDS = 1

export function isConversedCall(call: Pick<Call, 'status' | 'durationSeconds'>): boolean {
  return call.status === 'completed' && call.durationSeconds >= CONVERSED_CALL_MIN_SECONDS
}

export interface Transaction {
  id: string;
  providerTransactionId: string;
  /**
   * Id de PAGO del proveedor cuando difiere del id de checkout (Dodo:
   * payment_id `pay_…` ≠ checkout_session_id). Los refunds de Dodo referencian
   * ESTE id (ADR-015); null en RevenueCat/admin/referral.
   */
  providerPaymentId?: string | null;
  parentId: string;
  packageId: string;
  minutesPurchased: number;
  /**
   * Dinero COBRADO al comprador por el proveedor (ADR-021): se copia del evento, jamás se
   * deriva del catálogo de `pricing.ts` (que es escaparate). `null` cubre los dos
   * casos en que no hay importe que registrar: el checkout que aún no ha vuelto
   * ("no se sabe") y las filas sin dinero — crédito de admin, premio de referido,
   * reset de caducados — ("no hubo").
   */
  amount: number | null;
  currency: CurrencyCode | null;
  status: TransactionStatus;
  provider: TransactionProvider;
  metadata?: Record<string, any> | null;
  createdAt: Date;
  updatedAt: Date;
  /** Cuándo se liquidó (`completeAndCredit`); null si nunca. No uses `updatedAt` como hora de compra. */
  completedAt?: Date | null;
}

export type TransactionStatus =
  | "pending"
  | "completed"
  | "failed"
  | "refunded"
  | "admin_credit";

export interface Package {
  id: string;
  name: string;
  minutes: number;
  bonusMinutes?: number;
  priceUSD: number;
  priceEUR: number;
}

/**
 * Catálogo de packs. Importes desde `server/domain/pricing`, que resuelve el
 * override por env var.
 *
 * **Sin ids de producto** (ADR-021 regla 4): qué vende cada pack en cada
 * proveedor es configuración de ese proveedor, y vive en su adapter
 * (`server/infrastructure/dodo/dodo-products.ts`,
 * `lib/revenuecat-config.ts`). El dominio solo dice qué packs existen.
 */
export const MINUTE_PACKAGES: (Package & { id: PricedPackageId })[] = [
  {
    id: "package_3min",
    name: "Pack 3",
    minutes: 3,
    bonusMinutes: 1,
    priceUSD: getPrice("package_3min", "USD"),
    priceEUR: getPrice("package_3min", "EUR"),
  },
  {
    id: "package_6min",
    name: "Pack 6",
    minutes: 6,
    bonusMinutes: 1,
    priceUSD: getPrice("package_6min", "USD"),
    priceEUR: getPrice("package_6min", "EUR"),
  },
];

export const getPackageBonusMinutes = (pkg: Package): number => {
  return pkg.bonusMinutes ?? 0;
};

export const getPackageTotalMinutes = (pkg: Package): number => {
  return pkg.minutes + getPackageBonusMinutes(pkg);
};

export const getPackageMinutesLabel = (pkg: Package): string => {
  const bonus = getPackageBonusMinutes(pkg);
  return bonus > 0 ? `${pkg.minutes}+${bonus}` : `${pkg.minutes}`;
};

// DTOs para crear/actualizar
export type CreateChildInput = Omit<
  Child,
  "id" | "parentId" | "heygenContextId" | "createdAt"
>;
export type UpdateChildInput = Partial<CreateChildInput>;

// Untrusted input as received at the API/application boundary (e.g. from
// `await request.json()`). Every field is optional and loosely typed; the
// application layer validates it before constructing the domain DTO above.
export type RawChildInput = {
  name?: string;
  age?: number;
  gender?: string;
  language?: string;
  hobbies?: string | null;
  achievements?: string | null;
  behavior?: string | null;
  pets?: string | null;
  siblings?: string | null;
  christmasWishes?: string | null;
  additionalInfo?: string | null;
};

// Lead de la waitlist de marketing (lead magnet de las guías, capa 3).
// Email normalizado (trim+lowercase) como identidad; el consentimiento
// fechado es la base RGPD del futuro envío estacional.
export type MarketingLead = {
  email: string;
  locale: string;
  source: string;
  consentAt: Date;
  createdAt: Date;
  updatedAt: Date;
};
